import {
	DialogCloseError,
	DialogCloseResult,
	DialogController,
} from 'aurelia-dialog';
import {
	autoinject,
	bindable,
	computedFrom,
	containerless,
} from 'aurelia-framework';
import PerfectScrollbar from 'perfect-scrollbar';

import { ShortcutKey } from '~/constants';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { TooltipUtils } from '~/utils/tooltip-utils';

export enum DialogSize {
	EXTRA_SMALL = 'xs',
	SMALL = 'sm',
	MEDIUM = 'md',
	LARGE = 'lg',
	EXTRA_LARGE = 'xl',
}

export interface BaseDialogModel {
	/**
	 * Dialog size - used to apply styling on the dialog box
	 * @default DialogSize.MEDIUM
	 */
	dialogSize?: DialogSize;

	/**
	 * Extends the dialog to full height
	 * Applies `i-modal--fixed-footer` -- borrowed from OUI
	 * @default false
	 */
	fixedFooter?: boolean;

	/**
	 * Heading text shown on the modal
	 */
	headingText?: string;

	/**
	 * Body text
	 */
	bodyText?: string;

	/**
	 * 'Ok' button text
	 */
	okButtonText?: string;

	/**
	 * 'Cancel' button text
	 */
	cancelButtonText?: string;

	/**
	 * Adds `PerfectScrollbar` on the dialog body
	 * @default true
	 */
	scrollable?: boolean;
}

export const BaseDialogModelDefaults: BaseDialogModel = {
	bodyText: '',
	cancelButtonText: 'clickable-elements.cancel',
	dialogSize: DialogSize.MEDIUM,
	fixedFooter: false,
	okButtonText: 'clickable-elements.ok',
	scrollable: false,
};

@autoinject
@containerless
export abstract class BaseDialog<DialogModel extends BaseDialogModel> {
	/**
	 * Dialog events
	 */
	public static readonly Events = {
		CloseBaseDialog: 'Dialog.CloseBaseDialog',
	};
	/**
	 * Dialog sizes
	 */
	public static readonly DialogSize = DialogSize;

	/**
	 * The dialog view model
	 */
	@bindable protected model: DialogModel;

	/**
	 * jQuery element reference
	 *
	 * @deprecated Do not use jQuery to modify the DOM, modify data bindings that update the DOM.
	 */
	protected jQueryElement: JQuery<HTMLElement>;

	/**
	 * Dialog CSS selectors
	 */
	protected selectors = {
		dialogContainer: '.a-dialog',
		dialogBody: '.a-dialog__body',
		tooltip: 'button[data-toggle="tooltip"]',
	};

	/**
	 * Width classes applied to .i-modal based on `model.dialogSize`
	 */
	protected widthClasses: { [key in DialogSize]: string } = {
		xs: `i-modal--xs`,
		sm: `i-modal--sm`,
		md: `i-modal--md`,
		lg: `i-modal--lg`,
		xl: `i-modal--xl`,
	};

	/**
	 * Default class names for dialog/modal
	 */
	private classes = {
		base: 'modal modal-dialog i-modal a-dialog',
		fixedFooter: 'i-modal--fixed-footer',
		scrollableBody: 'a-dialog--scrollable',
	};

	constructor(
		protected readonly controller?: DialogController,
		protected readonly tooltipUtils?: TooltipUtils,
		protected readonly element?: Element,
	) {
		if (!this.controller || !this.tooltipUtils || !this.element) {
			// NOTE: Jake: 2019-01-18 - WEBUI-1131
			// Unfortunately we have to make all the arguments optional or else
			// we can't pass ...rest into it. (compile-time error)
			// I've added some manualy error handling here just in-case.
			throw new Error('Missing ...rest in subclass');
		}
	}

	protected activate(
		model: DialogModel,
		defaults: Partial<DialogModel> = BaseDialogModelDefaults as DialogModel,
	): Promise<DialogModel> {
		this.model = {
			...defaults,
			...model,
		};
		return Promise.resolve(this.model);
	}

	protected deactivate(_: DialogCloseResult | DialogCloseError): void {
		// no-op, this is staying around as sub-classes call super.deactivate()
	}

	protected attached(): void {
		this.jQueryElement = $(this.element) as JQuery<HTMLElement>;

		this.attachKeyboardShortcuts();

		if (this.model.scrollable) {
			const dialogBody = this.element.querySelector<HTMLElement>(
				this.selectors.dialogBody,
			);
			if (dialogBody) {
				new PerfectScrollbar(dialogBody, {
					wheelPropagation: true,
					useBothWheelAxes: true,
				});
			} else {
				Logger.warn(
					'Unable to get element:',
					this.selectors.dialogBody,
				);
			}
		}
	}

	protected detached(): void {
		KeyboardShortcut.unsubscribeAll(this);
	}

	protected attachKeyboardShortcuts(): void {
		// NOTE: Jake: 2019-12-12 -
		// Handle various modal templates based off base-dialog/simple-dialog.
		// We override this method for classic-panel-dialog.
		const okButtonEl = this.element.querySelectorAll(
			'[click\\.trigger="controller\\.ok()"]',
		);
		if (okButtonEl.length > 0) {
			KeyboardShortcut.subscribe(this, ShortcutKey.View, okButtonEl);
		}

		// NOTE(Jae): 2020-02-05 - WEBUI-2975
		// Support various close buttons
		let cancelButtonEl = this.element.querySelectorAll(
			'[click\\.trigger="controller\\.cancel()"]',
		);
		if (!cancelButtonEl || cancelButtonEl.length === 0) {
			cancelButtonEl = this.element.querySelectorAll(
				'[click\\.delegate="close($event)"]',
			);
			if (!cancelButtonEl || cancelButtonEl.length === 0) {
				// Support old "trigger" templates
				cancelButtonEl = this.element.querySelectorAll(
					'[click\\.trigger="close()"]',
				);
			}
		}
		if (cancelButtonEl.length > 0) {
			// NOTE(Zhanhuai): 2021-08-04
			// If multiple buttons are bound with the cancel handler,
			// just link keyboard short cut to the first of them. Otherwise KeyboardShortcut complains.
			KeyboardShortcut.subscribe(
				this,
				ShortcutKey.Close,
				Array.prototype.slice.call(cancelButtonEl, 0, 1),
			);
		} else {
			// NOTE: Jake: 2019-12-12
			// if no close button available, subscribe CLOSE to an empty function so that
			// anything behind the modal cant be triggered.
			KeyboardShortcut.subscribe(this, ShortcutKey.Close, () => {});
		}

		// NOTE(Jae): 2020-02-05 - WEBUI-2975
		// Add support for save button on various modals
		let saveButtonEl = this.element.querySelectorAll(
			'[click\\.delegate="save($event)"]',
		);
		if (!saveButtonEl || saveButtonEl.length === 0) {
			// Support old "trigger" templates
			saveButtonEl = this.element.querySelectorAll(
				'[click\\.trigger="save()"]',
			);
		}
		if (saveButtonEl.length > 0) {
			KeyboardShortcut.subscribe(this, ShortcutKey.Save, saveButtonEl);
		}
	}

	@computedFrom(
		'widthClasses',
		'model.dialogSize',
		'model.fixedFooter',
		'modal.scrollable',
	)
	get classNames(): string {
		return [
			this.classes.base,
			this.widthClasses[this.model.dialogSize],
			this.model.fixedFooter ? this.classes.fixedFooter : '',
			this.model.scrollable ? this.classes.scrollableBody : '',
		].join(' ');
	}

	@computedFrom('model.bodyText')
	get bodyText(): string {
		return this.model.bodyText;
	}

	@computedFrom('model.headingText')
	get headingText(): string {
		return this.model.headingText;
	}

	@computedFrom('model.okButtonText')
	get okButtonText(): string {
		return this.model.okButtonText;
	}

	@computedFrom('model.cancelButtonText')
	get cancelButtonText(): string {
		return this.model.cancelButtonText;
	}
}
export type BaseDialogConstructor<DialogModel extends BaseDialogModel> = new (
	...params: any[]
) => BaseDialog<DialogModel>;
